export type UploadEmployeesTranslation = {
  companyScope: {
    cancelButton: string;
    confirmButton: string;
    description: string;
    mandatoryTag: string;
    missingMandatoryCompany: string;
    readError: string;
    rowCount: string;
    rowsWithoutCompany: string;
    title: string;
  };
  companyScopeConfirm: {
    backButton: string;
    beyondMandatoryNotice: string;
    confirmButton: string;
    description: string;
    missing: {
      deactivateConsent: string;
      description: string;
      hideList: string;
      noName: string;
      sap: string;
      showList: string;
      title: string;
    };
    rowCount: string;
    title: string;
    total: string;
  };
  developmentCenterEdition: {
    cancelButton: string;
    confirmButton: string;
    description: string;
    editionYearLabel: string;
    overwriteNotice: string;
    positionNameLabel: string;
    positionNamePlaceholder: string;
    title: string;
    validation: {
      positionNameRequired: string;
      positionNameTooLong: string;
      yearOutOfRange: string;
      yearRequired: string;
    };
  };
  fileTypes: {
    CHAT_DOCUMENT: { description: string; label: string };
    COMPANY_POSITIONS: { description: string; label: string };
    DEVELOPMENT_ACTIVITIES: { description: string; label: string };
    DEVELOPMENT_CENTER: { description: string; label: string };
    EDUAKCJA: { description: string; label: string };
    EMPLOYEES: { description: string; label: string };
    EMPLOYEES_EMPLOYMENT_HISTORY: { description: string; label: string };
    EMPLOYEES_ENABLE_CHANGE: { description: string; label: string };
    MDR: { description: string; label: string };
    SYSTEM_USERS: { description: string; label: string };
  };
  form: {
    downloadFileTemplateLabel: string;
    dropzoneLabel: string;
    dropzoneLabel2: string;
    fileTypeDescription: string;
    resetButton: string;
    uploadButton: string;
    uploadFileLabel: string;
    uploadFileType: string;
  };
  mdrCareerPathsWarning: {
    cancelButton: string;
    confirmButton: string;
    description: string;
    manualPathsNotice: string;
    scopeNotice: string;
    title: string;
  };
  modal: {
    closeButton: string;
    downloadErrorButton: string;
    errorOccurred: string;
    errorTitle: string;
    readingCompaniesMessage: string;
    readingCompaniesTitle: string;
    uploadingMessage: string;
    uploadingTitle: string;
  };
  toast: {
    employeesUploadedFileError: string;
    employeesUploadedGeneralError: string;
    employeesUploadedSuccessfully: string;
  };
  validationErrors: {
    actions: {
      cancel: string;
      cannotResolveOtherType: string;
      chooseEachConflict: string;
      close: string;
      closeAndReupload: string;
      continue: string;
      replaceFile: string;
      resolve: string;
      resolveAll: string;
      resolveAllOfType: string;
      resolveAllVisible: string;
      skipAllOfType: string;
      viewDetails: string;
    };
    allErrorsResolvedMessage: string;
    allErrorsResolvedTitle: string;
    businessErrorTypes: {
      BIRTHDAY_DATE_CHANGED: string;
      COMPANY_POSITION_DATA: string;
      DEVELOPMENT_CENTER_DERIVED_VALUE_MISMATCH: string;
      DEVELOPMENT_CENTER_DUPLICATED_SAP: string;
      DEVELOPMENT_CENTER_INCOMPLETE_ROW: string;
      DEVELOPMENT_CENTER_INVALID_RATING: string;
      DEVELOPMENT_CENTER_INVALID_SAP: string;
      DEVELOPMENT_CENTER_INVALID_SESSION_DATE: string;
      DEVELOPMENT_CENTER_NOT_A_PARTICIPANT: string;
      DEVELOPMENT_CENTER_PARTICIPANT_NAME_MISMATCH: string;
      DEVELOPMENT_CENTER_REPORT_MISSING: string;
      DEVELOPMENT_CENTER_REPORT_NO_SAP_PREFIX: string;
      DEVELOPMENT_CENTER_REPORT_NOT_A_PDF: string;
      DEVELOPMENT_CENTER_REPORT_TOO_LARGE: string;
      DEVELOPMENT_CENTER_SCORE_OUT_OF_RANGE: string;
      DEVELOPMENT_CENTER_UNKNOWN_COMPETENCY: string;
      DEVELOPMENT_CENTER_UNKNOWN_EMPLOYEE: string;
      EMPLOYEE_EMAIL_CONFLICT: string;
      EMPLOYEE_ENABLE_INVALID_VALUE: string;
      EMPLOYEE_POSITION_CONSISTENCY: string;
      EMPLOYEE_REQUIRED_FIELDS: string;
      FUNCTIONAL_GROUP_LEVEL_HIERARCHY_INVALID: string;
      HRBP_CHANGED: string;
      OTHER: string;
      P1_SUPERVISOR_CHANGED: string;
      P2_SUPERVISOR_CHANGED: string;
      SAP_NUMBER_CHANGED: string;
      SYSTEM_USER_INVALID_ENABLED_VALUE: string;
    };
    columns: {
      actions: string;
      column: string;
      count: string;
      errorName: string;
      errorType: string;
      level: string;
      message: string;
      resolution: string;
      row: string;
      severity: string;
    };
    criticalErrorMessage: string;
    criticalErrorsFound: string;
    criticalErrorTitle: string;
    detailsTitle: string;
    goToPage: string;
    noErrors: string;
    replaceFileDialog: {
      acceptedFormats: string;
      description: string;
      dropzoneLabel: string;
      title: string;
      uploadButton: string;
    };
    resolution: {
      options: {
        ACCEPT_SAP_NUMBER: string;
        KEEP_SAP_NUMBER: string;
        SKIP_ROW: string;
      };
      placeholder: string;
      recommended: string;
      saveFailed: string;
    };
    skippedRowsMessage: string;
    skippedRowsTitle: string;
    status: {
      resolved: string;
    };
    subtitle: string;
    summaryTitle: string;
    title: string;
    uploadInfo: {
      fileName: string;
      fileType: string;
      title: string;
      uploadDate: string;
    };
    validationInfoMessage: string;
    validationInfoTitle: string;
  };
};

export const uploadEmployeesTranslation: UploadEmployeesTranslation = {
  companyScope: {
    cancelButton: 'Cancel',
    confirmButton: 'Import',
    description:
      'The file covers several companies. JMD is always imported; tick any other company whose people should be imported as well.',
    mandatoryTag: 'always imported',
    missingMandatoryCompany:
      'There are no JMD rows in this file. Only the companies you tick will be imported.',
    readError:
      'The companies could not be read from this file. Check that it is an employee file and try again.',
    rowCount: 'rows: {{rows}}',
    rowsWithoutCompany: 'Rows with no company are never imported. This file has {{rows}} of them.',
    title: 'Companies in the file',
  },
  companyScopeConfirm: {
    backButton: 'Back',
    beyondMandatoryNotice:
      'People outside JMD will become visible in the application, in employee search and in the organisational structure.',
    confirmButton: 'Import',
    description: 'The import will cover the people of these companies:',
    missing: {
      deactivateConsent:
        'Deactivate the people missing from the file ({{count}}) — they will disappear from the application',
      description:
        'They are active in the application, but the file no longer lists them, for example because they left the company. Without your consent the import leaves them active.',
      hideList: 'Hide the list',
      noName: 'No name',
      sap: 'SAP {{sap}}',
      showList: 'Show the list',
      title: 'Missing from the file: {{count}}',
    },
    rowCount: 'rows: {{rows}}',
    title: 'Confirm the scope of the import',
    total: 'Rows to import: {{rows}}',
  },
  developmentCenterEdition: {
    cancelButton: 'Cancel',
    confirmButton: 'Import',
    description:
      'The file does not carry the position or the edition it describes — provide them here. Together they identify the edition the results are saved under.',
    editionYearLabel: 'Edition year',
    overwriteNotice:
      'If a participant already has a result for this position and year, it will be overwritten. The previous version stays in the database as history.',
    positionNameLabel: 'Position',
    positionNamePlaceholder: 'e.g. SSM',
    title: 'Development Center edition',
    validation: {
      positionNameRequired: 'Enter the position name.',
      positionNameTooLong: 'The position name can be at most 128 characters.',
      yearOutOfRange: 'Enter a year between 2000 and {{maxYear}}.',
      yearRequired: 'Enter the edition year.',
    },
  },
  fileTypes: {
    CHAT_DOCUMENT: {
      description: 'Adds a document to the chat assistant knowledge base',
      label: 'Chat Document',
    },
    COMPANY_POSITIONS: {
      description: 'Updates company positions and organizational structure',
      label: 'Company Positions',
    },
    DEVELOPMENT_ACTIVITIES: {
      description:
        'Imports employee development activities (e.g. FRIS, BTM, Coaching) from a matrix file, matched by SAP number',
      label: 'Development Activities',
    },
    DEVELOPMENT_CENTER: {
      description:
        'Imports Development Center session results (competency and exercise scores, indicator ratings, key findings, self-awareness), matched by SAP number. You provide the position and the edition year during the import',
      label: 'Development Center',
    },
    EDUAKCJA: {
      description:
        'Replaces the training catalogue - rows are matched by object ID, trainings missing from the file are deleted',
      label: 'Education Data',
    },
    EMPLOYEES: {
      description: 'Updates basic employee information (name, SAP ID, position, department)',
      label: 'Employees',
    },
    EMPLOYEES_EMPLOYMENT_HISTORY: {
      description: 'Updates employee employment history and career paths',
      label: 'Employment History',
    },
    EMPLOYEES_ENABLE_CHANGE: {
      description: 'Updates employee status (active/inactive)',
      label: 'Employee Status',
    },
    MDR: {
      description: 'Updates MDR (Management Development Review) data',
      label: 'MDR Data',
    },
    SYSTEM_USERS: {
      description: 'Updates system users and access permissions',
      label: 'System Users',
    },
  },
  form: {
    downloadFileTemplateLabel: 'Download template file',
    dropzoneLabel: "Drag 'n' drop a file here, or click to select a file",
    dropzoneLabel2: '(accepted formats: .xls, .xlsx)',
    fileTypeDescription: 'What will be updated:',
    resetButton: 'Reset',
    uploadButton: 'Import',
    uploadFileLabel: 'Upload file',
    uploadFileType: 'File type to upload',
  },
  mdrCareerPathsWarning: {
    cancelButton: 'Cancel',
    confirmButton: 'Import and overwrite',
    description:
      'Career paths of the employees listed in the file will be replaced with the ones from the file. This applies both to the employee profile and to the succession module.',
    manualPathsNotice:
      'Career paths added manually in the succession module will be deleted for these employees. This cannot be undone.',
    scopeNotice: 'Employees who are not in the file remain unchanged.',
    title: 'Career paths will be overwritten',
  },
  modal: {
    closeButton: 'Close',
    downloadErrorButton: 'Download error',
    errorOccurred: 'An error occurred during upload',
    errorTitle: 'Upload Error',
    readingCompaniesMessage: 'Checking which companies the file covers, please wait...',
    readingCompaniesTitle: 'Reading the file',
    uploadingMessage: 'Uploading data, please wait...',
    uploadingTitle: 'Uploading Data',
  },
  toast: {
    employeesUploadedFileError:
      'Error occurred while uploading employees, the attached file is invalid.',
    employeesUploadedGeneralError:
      'Error occurred while uploading employees, please try again later',
    employeesUploadedSuccessfully: 'Employees uploaded successfully',
  },
  validationErrors: {
    actions: {
      cancel: 'Cancel',
      cannotResolveOtherType:
        'Cannot automatically resolve "Other" type errors - resolve them individually',
      chooseEachConflict: 'Choose a resolution for each conflict in the details, or skip them all',
      close: 'Close',
      closeAndReupload: 'Close and re-upload',
      continue: 'Continue',
      replaceFile: 'Replace file',
      resolve: 'Resolve',
      resolveAll: 'Resolve all',
      resolveAllOfType: 'Resolve all of this type',
      resolveAllVisible: 'Resolve all visible',
      skipAllOfType: 'Skip all rows of this type',
      viewDetails: 'View details',
    },
    allErrorsResolvedMessage:
      'All validation errors have been resolved. You can now continue with the upload.',
    allErrorsResolvedTitle: 'All errors resolved',
    businessErrorTypes: {
      BIRTHDAY_DATE_CHANGED: 'Birth date changed',
      COMPANY_POSITION_DATA: 'Company position data',
      DEVELOPMENT_CENTER_DERIVED_VALUE_MISMATCH: 'Sums and counters do not match the data',
      DEVELOPMENT_CENTER_DUPLICATED_SAP: 'SAP number duplicated in the file',
      DEVELOPMENT_CENTER_INCOMPLETE_ROW: 'Incomplete participant row',
      DEVELOPMENT_CENTER_INVALID_RATING: 'Rating outside the allowed scale',
      DEVELOPMENT_CENTER_INVALID_SAP: 'Missing or invalid SAP number',
      DEVELOPMENT_CENTER_INVALID_SESSION_DATE: 'Invalid session date',
      DEVELOPMENT_CENTER_NOT_A_PARTICIPANT: 'Person outside the other certification stages',
      DEVELOPMENT_CENTER_PARTICIPANT_NAME_MISMATCH: 'Participant name does not match the employee',
      DEVELOPMENT_CENTER_REPORT_MISSING: 'No individual report in the archive',
      DEVELOPMENT_CENTER_REPORT_NO_SAP_PREFIX: 'File name without a SAP number',
      DEVELOPMENT_CENTER_REPORT_NOT_A_PDF: 'File is not a PDF document',
      DEVELOPMENT_CENTER_REPORT_TOO_LARGE: 'Report exceeds the allowed size',
      DEVELOPMENT_CENTER_SCORE_OUT_OF_RANGE: 'Score outside the scale',
      DEVELOPMENT_CENTER_UNKNOWN_COMPETENCY: 'Unknown related competency',
      DEVELOPMENT_CENTER_UNKNOWN_EMPLOYEE: 'No employee with this SAP number',
      EMPLOYEE_EMAIL_CONFLICT: 'Same email address for different employees',
      EMPLOYEE_ENABLE_INVALID_VALUE: 'Invalid employee status value',
      EMPLOYEE_POSITION_CONSISTENCY: 'Employee position consistency',
      EMPLOYEE_REQUIRED_FIELDS: 'Required fields missing',
      FUNCTIONAL_GROUP_LEVEL_HIERARCHY_INVALID: 'Invalid functional group level hierarchy',
      HRBP_CHANGED: 'HRBP changed',
      OTHER: 'Other',
      P1_SUPERVISOR_CHANGED: 'P+1 supervisor changed',
      P2_SUPERVISOR_CHANGED: 'P+2 supervisor changed',
      SAP_NUMBER_CHANGED: 'SAP number changed',
      SYSTEM_USER_INVALID_ENABLED_VALUE: 'Invalid enabled value',
    },
    columns: {
      actions: 'Actions',
      column: 'Column',
      count: 'Count',
      errorName: 'Error name',
      errorType: 'Error type',
      level: 'Level',
      message: 'Message',
      resolution: 'Resolution',
      row: 'Row',
      severity: 'Level',
    },
    criticalErrorMessage:
      'The file contains structural errors (e.g. missing columns) and cannot be processed. Please fix the file and re-upload.',
    criticalErrorsFound: 'Critical errors found',
    criticalErrorTitle: 'Critical Validation Error',
    detailsTitle: 'Error details',
    goToPage: 'Go to page',
    noErrors: 'No errors',
    replaceFileDialog: {
      acceptedFormats: '(accepted formats: .xls, .xlsx)',
      description:
        'Upload a new file to replace the current one. Validation will restart from the beginning.',
      dropzoneLabel: "Drag 'n' drop a file here, or click to select a file",
      title: 'Replace File',
      uploadButton: 'Upload',
    },
    resolution: {
      options: {
        ACCEPT_SAP_NUMBER: 'Give the employee the new SAP number',
        KEEP_SAP_NUMBER: 'Keep the row with SAP number {{value}}',
        SKIP_ROW: 'Leave out of the import',
      },
      placeholder: 'Choose a resolution',
      recommended: 'Recommended',
      saveFailed: 'The choice was not saved. Try again.',
    },
    skippedRowsMessage: 'Rows that will not be imported when you continue: {{rows}}.',
    skippedRowsTitle: 'Some rows will be skipped',
    status: {
      resolved: 'Resolved',
    },
    subtitle: 'Review and resolve the errors detected during file validation',
    summaryTitle: 'Errors summary by type',
    title: 'Validation Errors',
    uploadInfo: {
      fileName: 'File name',
      fileType: 'File type',
      title: 'File Information',
      uploadDate: 'Upload date',
    },
    validationInfoMessage:
      'The file contains validation errors. You can resolve errors in the system or fix the file and re-upload.',
    validationInfoTitle: 'Validation Information',
  },
};
